// How a commit's checks stand, from its rollup's contexts (check runs and
// commit statuses together, as GitHub's statusCheckRollup holds them): one
// rule for whether a context failed or is still running, which both the
// state and the failing count read, so the two never disagree.
import type { CheckContext } from '../api/queries';

/** How a commit's checks stand, as one word. */
export type ChecksState = 'success' | 'failure' | 'pending' | 'none';

/** A check run's conclusion, or a commit status's state, that fails. */
const FAILED = new Set(['FAILURE', 'ERROR', 'TIMED_OUT', 'ACTION_REQUIRED', 'CANCELLED', 'STARTUP_FAILURE']);

/** Whether a context failed: a check run's conclusion, or a status's state. */
export function contextFails(c: CheckContext): boolean {
  return FAILED.has((c.__typename === 'StatusContext' ? c.state : c.conclusion) ?? '');
}

/** Whether a context is still running: a check run not completed, or a status pending (or expected). */
export function contextPending(c: CheckContext): boolean {
  return c.__typename === 'StatusContext' ? c.state === 'PENDING' || c.state === 'EXPECTED' : c.status !== 'COMPLETED';
}

/** The checks' state: any failing context fails them, any running one leaves them pending, none is none. */
export function checksState(contexts: readonly CheckContext[]): ChecksState {
  if (!contexts.length) return 'none';
  if (contexts.some(contextFails)) return 'failure';
  if (contexts.some(contextPending)) return 'pending';
  return 'success';
}

/** How many contexts fail, by the rule `checksState` reads. */
export const failingCount = (contexts: readonly CheckContext[]): number => contexts.filter(contextFails).length;
